import { TWidget } from 'widgets/Widget';
import { RefElement } from 'widgets/toolbox/RefElement';
import { isLargeViewAndUp } from 'widgets/toolbox/viewtype';

const DATA_HOTSPOT_BTN = '[data-ref="hotSpotBtn"]';
const CLASS_HOTSPOTS_CONTAINER = '.b-hots_container';
const ATTRIBUTE_ARIA_HIDDEN = 'aria-hidden';
const keyCode = Object.freeze({
    ESCAPE: 27,
    RETURN: 13
});

/**
 * @param Widget Base widget for extending
 * @returns HotSpot class
 */

function HotSpotClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory plugin_page_designer_ext
     * @class HotSpot
     * @augments Widget
     * @classdesc Represents HotSpot component with feature:
     * 1. Show and hide popup
     *
     * <div class="b-hots_container">
     *     <div class="b-hot_spot ${pdict.mobileHide}" style="${pdict.styles}" data-widget="hotSpot"
     *         data-event-keydown="handleKeydown">
     *         <button class="b-hot_spot-btn" data-ref="hotSpotBtn" data-event-click="handleHotspotInteraction"
     *             title="${Resource.msg('common.close', 'common', null)}" tabindex="0" aria-haspopup="true"
     *             aria-label="${Resource.msg('hotspot.show', 'pagedesigner', null)}">
     *             <span class="b-hot_spot-sign"></span>
     *         </button>
     *         <div class="b-hot_spot-popup" data-ref="hotSpotPopup" tabindex="-1" aria-hidden="true">
     *             <button class="b-hot_spot-close" title="${Resource.msg('common.close', 'common', null)}"
     *                     data-event-click="closePopup" aria-label="${Resource.msg('common.close', 'common', null)}">
     *                     <isinclude template="/common/icons/standalone/close" />
     *             </button>
     *             <div class="b-hot_spot-scrollbox">
     *                     <div class="b-hot_spot-content"></div>
     *             </div>
     *         </div>
     *         <div class="b-hot_spot-overlay" data-event-click="closePopup"></div>
     *     </div>
     * </div>
     */

    class HotSpot extends Widget {
        timeout?: ReturnType<typeof setTimeout>;

        hotSpots!: NodeListOf<HTMLElement>;

        hotSpotButtons!: Array<HTMLButtonElement>;

        prefs() {
            return {
                active: 'm-active',
                ...super.prefs()
            };
        }

        init() {
            super.init();

            const hotSpot = this.ref('self').get();
            const container = hotSpot?.closest(CLASS_HOTSPOTS_CONTAINER) as HTMLElement | null;
            const closeAllPopupsBtn = container ? container.querySelector('[data-action="close-all-popups"]') : null;

            if (closeAllPopupsBtn) {
                closeAllPopupsBtn.addEventListener('click', this.closeAllPopups.bind(this));
            }
        }

        /**
         * @description Set tabindex value
         * @param indexValue attribute value
         */
        toggleButtonTabindex(indexValue) {
            const container = this.ref('self').get()?.closest(CLASS_HOTSPOTS_CONTAINER) as HTMLElement;
            const hotSpotButtons = container ? container.querySelectorAll(DATA_HOTSPOT_BTN) : null;

            if (hotSpotButtons) {
                hotSpotButtons.forEach(hotSpotButton => {
                    hotSpotButton.setAttribute('tabindex', indexValue);
                });
            }
        }

        /**
         * @description Close all hotspot popups
         */
        closeAllPopups() {
            const container = this.ref('hotSpotBtn').get()?.closest(CLASS_HOTSPOTS_CONTAINER) as HTMLElement | null;
            const hotSpotButtons = container ? container.querySelectorAll(DATA_HOTSPOT_BTN) : null;

            // early return
            if (!hotSpotButtons) {
                return;
            }

            hotSpotButtons.forEach(item => {
                const hotSpotEl = item.closest('[data-widget="hotSpot"]');
                const hotSpotPopupEl = hotSpotEl ? hotSpotEl.querySelector('[data-ref="hotSpotPopup"]') : null;

                // early return
                if (!hotSpotPopupEl) {
                    return;
                }

                const hotSpotBtn = new RefElement<HTMLButtonElement>([item as HTMLButtonElement]);
                const hotSpotPopup = new RefElement<HTMLElement>([hotSpotPopupEl as HTMLElement]);

                hotSpotBtn.removeClass(this.prefs().active);
                hotSpotPopup.attr(ATTRIBUTE_ARIA_HIDDEN, 'true');

                item.setAttribute('tabindex', '0');
            });
        }

        /**
         * @description Sets Auto Placment of Popup
         */
        setPopupAutoPlacement() {
            const popup = this.ref('hotSpotPopup').get() as HTMLElement;
            const btn = this.ref('hotSpotBtn').get() as HTMLButtonElement;
            const container = btn.closest(CLASS_HOTSPOTS_CONTAINER) as HTMLElement;

            // early return
            if (!popup || !btn || !container) {
                return;
            }

            const btnRect = btn.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            const topSpace = Math.max(0, btnRect.top - containerRect.top);
            const rightSpace = Math.max(0, containerRect.right - btnRect.right);
            const bottomSpace = Math.max(0, containerRect.bottom - btnRect.bottom);
            const leftSpace = Math.max(0, btnRect.left - containerRect.left);

            const buttonWidth = btn.offsetWidth;
            const buttonHeight = btn.offsetHeight;
            const popupWidth = popup.offsetWidth;
            const popupHeight = popup.offsetHeight;
            const margin = 8;

            let transformX = '';
            let transformY = '';

            // sets default styles
            popup.style.top = 'auto';
            popup.style.right = 'auto';
            popup.style.left = 'auto';
            popup.style.transform = 'none';

            // vertical position
            if (topSpace >= popupHeight) {
                popup.style.top = `-${popupHeight}px`;
            } else if (bottomSpace >= popupHeight) {
                popup.style.top = `${buttonHeight}px`;
            } else if (topSpace >= popupHeight / 2 && bottomSpace >= popupHeight / 2) {
                popup.style.top = `${buttonHeight / 2}px`;
                transformY = 'translateY(-50%)';
            } else {
                popup.style.top = `${-(topSpace - margin)}px`;
            }

            // horizontal position
            if (leftSpace >= popupWidth / 2 && rightSpace >= popupWidth / 2 && !transformY) {
                popup.style.left = `${buttonWidth / 2}px`;
                transformX = 'translateX(-50%)';
            } else if (rightSpace >= popupWidth + margin) {
                popup.style.left = `${buttonWidth + margin}px`;
            } else if (leftSpace >= popupWidth + margin) {
                popup.style.right = `${buttonWidth + margin}px`;
            } else if (leftSpace >= popupWidth / 2 && rightSpace >= popupWidth / 2) {
                popup.style.left = `${buttonWidth / 2}px`;
                transformX = 'translateX(-50%)';
            } else {
                popup.style.left = `${-(leftSpace - margin)}px`;
            }

            popup.style.transform = `${transformX} ${transformY}`.trim();
        }

        /**
         * @description Open hotspot popup
         */
        openPopup() {
            if (this.ref('hotSpotPopup').data('auto-placement')) {
                this.closeAllPopups();
                this.setPopupAutoPlacement();
            }

            this.ref('hotSpotBtn').addClass(this.prefs().active);
            this.ref('hotSpotPopup').attr(ATTRIBUTE_ARIA_HIDDEN, 'false');
            this.toggleButtonTabindex(-1);

            if (this.timeout) {
                clearTimeout(this.timeout);
            }

            this.timeout = setTimeout(() => {
                if (this.ref('hotSpotPopup').data('prevent-focus-scroll')) {
                    this.ref('hotSpotPopup').focus({ preventScroll: true });
                } else {
                    this.ref('hotSpotPopup').focus();
                }
            }, 0);
        }

        /**
         * @description Close hotspot popup
         * @param _el Source of Keydown event
         * @param event event instance if DOM event
         */
        closePopup(_el, event) {
            // We need this to have click on Page Designer.
            // Use mousedown,touchstart since click overloaded.
            // Check that it is not triggered by mouse buttons.
            // Restricted btn: mouse right and center. True - if restricted btn was clicked
            if (event.button) {
                return;
            }

            this.ref('hotSpotBtn').removeClass(this.prefs().active);
            this.ref('hotSpotPopup').attr(ATTRIBUTE_ARIA_HIDDEN, 'true');
            this.toggleButtonTabindex(0);

            if (!this.ref('hotSpotPopup').data('prevent-focus-scroll') || isLargeViewAndUp()) {
                this.ref('hotSpotBtn').focus();
            }
        }

        /**
         * @description Handle hotSpot click
         * @param _el Source of Keydown event
         * @param event event instance if DOM event
         */
        handleHotspotInteraction(_el, event) {
            // We need this to have click on Page Designer.
            // Use mousedown,touchstart since click overloaded.
            // Check that it is not triggered by mouse buttons.
            // Restricted btn: mouse right and center. True - if restricted btn was clicked
            if (event.button) {
                return;
            }

            const hotSpot = this.ref('self').get();
            const productTileName = (hotSpot?.querySelector('[data-product-name]') as HTMLElement)?.dataset?.productName;
            const textBlockValue = hotSpot?.querySelectorAll('h1, h2, h3')[0]?.textContent;

            this.openPopup();
            this.eventBus().emit('hotSpot.click', productTileName || textBlockValue);
        }

        /**
         * @param _el Source of Keydown event
         * @param event event instance if DOM event
         */
        handleKeydown(_el, event) {
            if (!event) {
                return;
            }

            if (event.keyCode === keyCode.ESCAPE) {
                event.preventDefault();
                event.stopPropagation();

                this.closePopup(_el, event);
            }
        }
    }

    return HotSpot;
}

export type THotSpot = ReturnType<typeof HotSpotClassCreator>;

export type THotSpotInstance = InstanceType<THotSpot>;

export default HotSpotClassCreator;
